import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const WHATSAPP_NUMBER = '905325416344';
const PHONE_NUMBER = '05325416344';
const PHONE_HREF = 'tel:+905325416344';
const WA_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=Merhaba%2C%20bilgi%20almak%20istiyorum.`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Ana Sayfa', path: '/' },
    { label: 'İkinci El Satış', path: '/ikinci-el' },
    { label: 'Yazılım', path: '/yazilim' },
    { label: 'İletişim', path: '/iletisim' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white border-b border-slate-100' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer flex-shrink-0">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${scrolled ? 'bg-orange-500' : 'bg-orange-500'}`}>
                <i className="ri-computer-line text-white text-lg"></i>
              </div>
              <div>
                <span className={`font-extrabold text-base leading-none block ${scrolled ? 'text-slate-900' : 'text-white'}`}>
                  Teknik Servis
                </span>
                <span className={`text-xs leading-none ${scrolled ? 'text-orange-500' : 'text-orange-300'}`}>
                  Bilgisayar &amp; Yazılım
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    isActive(link.path)
                      ? scrolled
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/20 text-white'
                      : scrolled
                      ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right: Contact Buttons + Panel */}
            <div className="hidden md:flex items-center gap-2">
              {/* WhatsApp */}
              <a
                href={WA_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-green-500/90 hover:bg-green-500 text-white'
                }`}
              >
                <i className="ri-whatsapp-line text-base"></i>
                <span>WhatsApp</span>
              </a>

              {/* Phone */}
              <a
                href={PHONE_HREF}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-orange-500/90 hover:bg-orange-500 text-white'
                }`}
              >
                <i className="ri-phone-line text-base"></i>
                <span>{PHONE_NUMBER}</span>
              </a>

              {/* Panel */}
              <Link
                to="/panel"
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700'
                    : 'border-white/30 text-white/70 hover:border-white/60 hover:text-white'
                }`}
              >
                <i className="ri-settings-3-line"></i>
              </Link>
            </div>

            {/* Mobile: WA + Hamburger */}
            <div className="md:hidden flex items-center gap-2">
              <a
                href={WA_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-green-500 text-white cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg"></i>
              </a>
              <a
                href={PHONE_HREF}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-500 text-white cursor-pointer"
              >
                <i className="ri-phone-line text-lg"></i>
              </a>
              <button
                className={`p-2 rounded-lg cursor-pointer ${scrolled ? 'text-slate-700' : 'text-white'}`}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="md:hidden bg-white rounded-xl border border-slate-100 mb-4 overflow-hidden">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-5 py-3.5 text-sm font-semibold border-b border-slate-50 cursor-pointer ${
                    isActive(link.path) ? 'text-orange-500 bg-orange-50' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="p-3 flex gap-2">
                <a
                  href={WA_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-green-500 text-white py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
                <a
                  href={PHONE_HREF}
                  className="flex-1 bg-orange-500 text-white py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i> Ara
                </a>
              </div>
              <Link
                to="/panel"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-slate-400 border-t border-slate-50 cursor-pointer"
              >
                <i className="ri-settings-3-line"></i>
                Yönetim Paneli
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Floating WhatsApp Button (mobile) */}
      <a
        href={WA_HREF}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center rounded-full bg-green-500 hover:bg-green-600 text-white cursor-pointer md:hidden"
        style={{ boxShadow: '0 4px 20px rgba(34,197,94,0.4)' }}
        title="WhatsApp ile iletişime geç"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </>
  );
};

export default Navbar;
